import React from 'react';
import { Image } from '../../../commonTypes/image';
import { CallToAction, TextLink } from '../../../commonTypes/links';
import colors from '../../../helpers/colors';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme } from '../../../theme';
import { LottieAnimation } from '../../Lottie';
import ChevronBackground from '../ChevronBackground';
import { HeroPromo, HeroPromoProps, HeroWrapper } from '../HeroPromo';
import {
  CoBrand,
  CoBrandImage,
  CoBrandText,
  CoBrandWrapper,
  ContentGridItem,
  CtaWrapper,
  DesktopImageGridItem,
  FinePrint,
  Heading,
  HeroGrid,
  ImageWrapper,
  MainAnimation,
  MainImage,
  PageTitle,
  PageType,
  RichTextSubhead,
  StyledPrimaryCta,
  StyledSecondaryCta,
  TertiaryLink,
  TrustSymbol,
} from './styles';

export const HERO_BANNER_CTAS_ID = 'hero-banner__ctas';

export type HeroWithBackgroundProps = {
  pageType: PageType;
  pageTitle?: string;
  heading: string;
  useHeadingAsH1?: boolean;
  subhead?: string;
  image?: Image;
  animation?: LottieAnimation;
  finePrint?: string;
  primaryCta?: CallToAction;
  secondaryCta?: CallToAction;
  trustSymbol?: Image;
  coBrandTag?: CoBrandTag;
  marketingPromo?: HeroPromoProps;
  link?: TextLink;
} & ComponentProps;

export type CoBrandTag = {
  text: string;
  logo: Image;
};

type HeroImageProps = {
  pageType?: PageType;
  animation?: LottieAnimation;
  image?: Image;
};

const HeroImage: React.FC<HeroImageProps> = ({ image, animation, pageType }) => {
  if (animation) {
    return <MainAnimation animation={animation} pageType={pageType} />;
  }
  if (image) {
    return <MainImage image={image} pageType={pageType} shouldEagerLoad={true} />;
  }
  return null;
};

/** Banner with a chevron background and complex content */
const HeroWithBackground: React.FC<HeroWithBackgroundProps> = ({
  pageType,
  pageTitle,
  heading,
  useHeadingAsH1,
  subhead,
  finePrint,
  image,
  animation,
  primaryCta,
  secondaryCta,
  trustSymbol,
  coBrandTag,
  marketingPromo,
  link,
  ...props
}) => {
  const heroImage = animation || image;
  const desktopImageGridWidth = pageType === 'primary' ? 6 : 4;
  const desktopContentGridWidth = heroImage ? 12 - desktopImageGridWidth : 10;

  return (
    <HeroWrapper {...componentProps(props)}>
      <ChevronBackground>
        <HeroGrid alignLeft>
          {(animation || image) && (
            <DesktopImageGridItem
              desktop={desktopImageGridWidth}
              tabletLandscape={desktopImageGridWidth}>
              <ImageWrapper>
                <HeroImage animation={animation} image={image} pageType={pageType} />
                {coBrandTag && (
                  <CoBrandWrapper>
                    <CoBrand>
                      <CoBrandText>{coBrandTag.text}</CoBrandText>
                      <CoBrandImage image={coBrandTag.logo} shouldEagerLoad={true} />
                    </CoBrand>
                  </CoBrandWrapper>
                )}
              </ImageWrapper>
            </DesktopImageGridItem>
          )}
          <ContentGridItem
            desktop={desktopContentGridWidth}
            tabletLandscape={desktopContentGridWidth}>
            {pageTitle && (
              <PageTitle as={useHeadingAsH1 ? 'div' : 'h1'}>{pageTitle}</PageTitle>
            )}
            <Heading as={useHeadingAsH1 ? 'h1' : 'h2'}>{heading}</Heading>
            {trustSymbol && <TrustSymbol image={trustSymbol} shouldEagerLoad={true} />}
            {subhead && <RichTextSubhead html={subhead} customBulletIcon="chevron" />}
            {(animation || image) && (
              <ImageWrapper>
                <HeroImage animation={animation} image={image} pageType={pageType} />
                {coBrandTag && (
                  <CoBrandWrapper>
                    <CoBrand>
                      <CoBrandText>{coBrandTag.text}</CoBrandText>
                      <CoBrandImage image={coBrandTag.logo} shouldEagerLoad={true} />
                    </CoBrand>
                  </CoBrandWrapper>
                )}
              </ImageWrapper>
            )}
            <FinePrint>{finePrint}</FinePrint>
            {primaryCta && (
              <CtaWrapper id={HERO_BANNER_CTAS_ID}>
                <StyledPrimaryCta background="dark" cta={primaryCta} />
                {secondaryCta && (
                  <StyledSecondaryCta background="dark" cta={secondaryCta} />
                )}
              </CtaWrapper>
            )}
            {link && (
              <TertiaryLink
                href={link.url}
                onClick={link.onClick}
                color={colors.neutral08}>
                {link.text}
              </TertiaryLink>
            )}
          </ContentGridItem>
        </HeroGrid>
      </ChevronBackground>
      {marketingPromo && <HeroPromo {...marketingPromo} />}
    </HeroWrapper>
  );
};

export default applyComponentTheme(HeroWithBackground, 'HeroWithBackground');
